import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  IconButton,
  Chip,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Alert,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useSavedRemarks } from '../../../hooks/useSavedRemarks';
import { SavedRemark, SavedRemarkFile } from '../../../types/savedRemarks';
import RemarkForm from './RemarkForm';
import SavedRemarkListItem from './SavedRemarkListItem';

interface SavedRemarksDialogProps {
  open: boolean;
  onClose: () => void;
  onInsertRemark: (content: string, price?: number, files?: SavedRemarkFile[], remarkId?: number) => void;
}

type SortOption = 'recent' | 'usage' | 'alphabetical';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'image/jpeg',
  'image/png',
  'image/gif'
];

/** Search, sort, "Add New" and the count, pinned above the remark list. */
function SavedRemarksToolbar({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  onAddNew,
  addDisabled,
  shownCount,
  totalRemarks,
}: {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  onAddNew: () => void;
  addDisabled: boolean;
  shownCount: number;
  totalRemarks: number;
}) {
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        bgcolor: 'background.paper',
        pt: 2,
        pb: 1.5,
        mb: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: totalRemarks > 0 ? 1.5 : 0, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by title, content, or price..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: '1 1 260px' }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value as SortOption)}
            startAdornment={<FilterIcon fontSize="small" sx={{ mr: 1 }} />}
          >
            <MenuItem value="recent">Recent</MenuItem>
            <MenuItem value="usage">Most Used</MenuItem>
            <MenuItem value="alphabetical">A-Z</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddNew}
          disabled={addDisabled}
        >
          Add New
        </Button>
      </Box>

      {totalRemarks > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`${shownCount} of ${totalRemarks} remarks`} size="small" />
          {searchQuery && (
            <Chip
              label={`Search: "${searchQuery}"`}
              size="small"
              onDelete={() => onSearchChange('')}
              variant="outlined"
            />
          )}
        </Box>
      )}
    </Box>
  );
}

const SavedRemarksDialog: React.FC<SavedRemarksDialogProps> = ({
  open,
  onClose,
  onInsertRemark,
}) => {
  const {
    loading,
    error,
    addRemark,
    updateRemark,
    deleteRemark,
    incrementUsageCount,
    searchRemarks,
    getSortedRemarks,
    refetchRemarks,
    addFilesToRemark,
    deleteFileFromRemark,
    getFileDownloadUrl,
    totalRemarks,
  } = useSavedRemarks();

  // UI State
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state for adding/editing
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formFiles, setFormFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string>('');
  const [existingFiles, setExistingFiles] = useState<SavedRemarkFile[]>([]);

  // Filtered and sorted remarks
  const filteredRemarks = searchQuery ? searchRemarks(searchQuery) : getSortedRemarks(sortBy);

  const resetForm = () => {
    setFormTitle('');
    setFormContent('');
    setFormPrice('');
    setFormFiles([]);
    setExistingFiles([]);
    setFileUploadError('');
  };

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setIsAddingNew(false);
      setEditingId(null);
      resetForm();
    }
  }, [open]);

  const handleAddNew = () => {
    setIsAddingNew(true);
    resetForm();
  };

  const handleEdit = (remark: SavedRemark) => {
    setEditingId(remark.id);
    setFormTitle(remark.title);
    setFormContent(remark.content);
    setFormPrice(remark.price?.toString() || '');
    setFormFiles([]); // Clear new files
    setExistingFiles(remark.files || []); // Set existing files for management
    setFileUploadError('');
  };

  const handleSaveNew = async () => {
    if (formTitle.trim() && formContent.trim()) {
      setIsSubmitting(true);
      try {
        const price = formPrice.trim() ? parseFloat(formPrice) : undefined;
        const newRemark = await addRemark(formTitle, formContent, price, formFiles);
        if (newRemark) {
          setIsAddingNew(false);
          resetForm();
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSaveEdit = async () => {
    if (editingId && formTitle.trim() && formContent.trim()) {
      setIsSubmitting(true);
      try {
        // null, not undefined: a cleared price field must clear the saved
        // price, or it keeps overwriting every quote the remark is used on
        const price = formPrice.trim() ? parseFloat(formPrice) : null;
        const success = await updateRemark(editingId, {
          title: formTitle,
          content: formContent,
          price: price,
        });
        
        // Add new files if any were selected
        if (success && formFiles.length > 0) {
          await addFilesToRemark(editingId, formFiles);
          // Refetch to get updated data with new files
          await refetchRemarks();
        }
        
        if (success) {
          setEditingId(null);
          resetForm();
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancelForm = () => {
    setIsAddingNew(false);
    setEditingId(null);
    resetForm();
  };

  // File handling functions
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      
      const invalidFiles = fileArray.filter(file => {
        return file.size > MAX_FILE_SIZE || !ALLOWED_FILE_TYPES.includes(file.type);
      });
      
      if (invalidFiles.length > 0) {
        setFileUploadError(`Some files are too large (>10MB) or have unsupported formats. Supported: PDF, DOC, DOCX, XLS, XLSX, TXT, JPG, PNG, GIF`);
        return;
      }
      
      setFileUploadError('');
      setFormFiles(fileArray);
    }
    
    // Clear the input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setFormFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingFile = async (fileId: number) => {
    const success = await deleteFileFromRemark(fileId);
    if (success) {
      // Update existing files state immediately for better UX
      setExistingFiles(prev => prev.filter(f => f.id !== fileId));
      // Also refetch to update the main remarks list
      await refetchRemarks();
    }
  };

  const openFile = async (file: SavedRemarkFile) => {
    const url = await getFileDownloadUrl(file);
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleDelete = async (id: number) => {
    await deleteRemark(id);
  };

  const handleInsert = async (remark: SavedRemark) => {
    await incrementUsageCount(remark.id);
    onInsertRemark(remark.content, remark.price, remark.files, remark.id);
    onClose();
  };

  // Shared by the add and edit forms; each adds its own Save (and edit its existing files).
  const formProps = {
    title: formTitle,
    onTitleChange: setFormTitle,
    content: formContent,
    onContentChange: setFormContent,
    price: formPrice,
    onPriceChange: setFormPrice,
    files: formFiles,
    onFilesSelected: handleFileChange,
    onRemoveFile: handleRemoveFile,
    fileUploadError,
    isSubmitting,
    onCancel: handleCancelForm,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': fullScreen
          ? {}
          : { height: 'min(85vh, 960px)', maxWidth: { md: 900, xl: 1040 } },
      }}
    >
      <DialogTitle
        component="div"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 1.5 }}
      >
        <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
          Saved Remarks
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close saved remarks">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, pt: 0, pb: 2 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Header with search and controls */}
        <SavedRemarksToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onAddNew={handleAddNew}
          addDisabled={isAddingNew || editingId !== null || loading}
          shownCount={filteredRemarks.length}
          totalRemarks={totalRemarks}
        />

        {/* Add new form */}
        {isAddingNew && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Add New Remark</Typography>
            <RemarkForm mode="add" {...formProps} onSave={handleSaveNew} />
          </Paper>
        )}

        {/* Remarks list */}
        <Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading saved remarks...</Typography>
            </Box>
          ) : filteredRemarks.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {totalRemarks === 0 
                ? "No saved remarks yet. Add your first remark to get started!"
                : "No remarks match your search criteria."
              }
            </Alert>
          ) : (
            <List sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filteredRemarks.map((remark) => (
                <React.Fragment key={remark.id}>
                  {editingId === remark.id ? (
                    // Edit form
                    <ListItem
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        bgcolor: 'grey.50',
                        border: 1,
                        borderColor: 'primary.light',
                        borderRadius: 1,
                        p: 2,
                      }}
                    >
                      <RemarkForm
                        mode="edit"
                        {...formProps}
                        existingFiles={existingFiles}
                        onOpenExistingFile={openFile}
                        onDeleteExistingFile={handleDeleteExistingFile}
                        onSave={handleSaveEdit}
                      />
                    </ListItem>
                  ) : (
                    // Display mode
                    <SavedRemarkListItem
                      remark={remark}
                      actionsDisabled={editingId !== null || isAddingNew || loading || isSubmitting}
                      canDeleteFiles={editingId !== remark.id}
                      onInsert={() => handleInsert(remark)}
                      onEdit={() => handleEdit(remark)}
                      onDelete={() => handleDelete(remark.id)}
                      onOpenFile={openFile}
                      onDeleteFile={handleDeleteExistingFile}
                    />
                  )}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SavedRemarksDialog;
